import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getInvestments } from '../../../../redux/actions/SocietyPortal/InvestmentAction/InvestmentAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import InvestmentModal from './InvestmentModal';
import InvestmentViewModal from './InvestmentViewModal';
import InvestmentActionModal from './InvestmentActionModal';
import InvestmentDeleteModal from './InvestmentDeleteModal';
import InvestmentValuationModal from './InvestmentValuationModal';
import { INVESTMENT_TYPES, MARKET_LINKED, typeMeta } from './investmentTypes';
import axiosInstance from '../../../../axios/axiosInstance';
import apiEndpoints from '../../../../axios/api';

const STATUS_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'matured', label: 'Matured' },
    { value: 'pre_matured', label: 'Pre-Matured' },
    { value: 'renewed', label: 'Renewed' },
];

const FREQUENCY_LABELS = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    half_yearly: 'Half-Yearly',
    annual: 'Annual',
    bullet: 'Bullet',
    quarter_end: 'Quarter End',
    month_end: 'Month End',
};

const INTEREST_MODE_LABELS = {
    simple: 'Simple',
    compound: 'Compound',
};

const STATUS_BADGE = {
    active: { bg: 'tw-bg-emerald-50', text: 'tw-text-emerald-600', border: 'tw-border-emerald-100' },
    matured: { bg: 'tw-bg-blue-50', text: 'tw-text-blue-600', border: 'tw-border-blue-100' },
    pre_matured: { bg: 'tw-bg-amber-50', text: 'tw-text-amber-600', border: 'tw-border-amber-100' },
    renewed: { bg: 'tw-bg-purple-50', text: 'tw-text-purple-600', border: 'tw-border-purple-100' },
    deleted: { bg: 'tw-bg-gray-50', text: 'tw-text-gray-500', border: 'tw-border-gray-100' },
};

const TYPE_OPTIONS = [{ value: 'all', label: 'All types' }, ...INVESTMENT_TYPES.map(t => ({ value: t.value, label: t.label }))];

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d)) return date;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}-${mm}-${d.getFullYear()}`;
};

const Investments = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { listData, loading } = useSelector(state => state.InvestmentReducer || {});

    const [status, setStatus] = useState('all');
    const [search, setSearch] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [investmentType, setInvestmentType] = useState('all');
    const [maturingSoon, setMaturingSoon] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState({ status: 'all', search: '', fromDate: '', toDate: '', investment_type: 'all', maturingSoon: false });
    const [summary, setSummary] = useState(null);
    const [valuationTarget, setValuationTarget] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [viewId, setViewId] = useState(null);
    const [actionState, setActionState] = useState({ action: null, investment: null });
    const [deleteTarget, setDeleteTarget] = useState(null);

    const investments = Array.isArray(listData) ? listData : [];

    const fetchList = (filters = appliedFilters) => {
        if (!societyId) return;
        dispatch(getInvestments({
            societyId,
            status: filters.status,
            search: filters.search || undefined,
            fromDate: filters.fromDate || undefined,
            toDate: filters.toDate || undefined,
            investment_type: filters.investment_type,
            maturing_within_days: filters.maturingSoon ? 30 : undefined,
        }));
        // Portfolio totals (active investments, all types).
        axiosInstance.get(apiEndpoints.GET_INVESTMENT_SUMMARY_API.replace(':societyId', societyId), { params: { maturing_within_days: 30 } })
            .then(res => setSummary(res?.data || null))
            .catch(() => setSummary(null));
    };
    usePageRefresh(() => fetchList());

    useEffect(() => {
        fetchList(appliedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, societyId, appliedFilters]);

    // Auto-apply status and dates
    useEffect(() => {
        setAppliedFilters(prev => ({ ...prev, status, fromDate, toDate, investment_type: investmentType, maturingSoon }));
    }, [status, fromDate, toDate, investmentType, maturingSoon]);

    const handleApplySearch = () => setAppliedFilters(prev => ({ ...prev, search }));
    const handleRefresh = () => fetchList();

    const handleAdd = () => {
        setEditId(null);
        setShowModal(true);
    };

    const handleEdit = (id) => {
        setEditId(id);
        setShowModal(true);
    };

    const handleView = (id) => {
        setViewId(id);
    };

    const handleAction = (action, investment) => {
        setActionState({ action, investment });
    };

    const handleActionSuccess = () => {
        setActionState({ action: null, investment: null });
        fetchList();
    };

    const handleDelete = (investment) => {
        setDeleteTarget(investment);
    };

    const handleDeleteSuccess = () => {
        setDeleteTarget(null);
        fetchList();
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditId(null);
    };

    const handleSaveSuccess = () => {
        handleCloseModal();
        fetchList();
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                {/* Header */}
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-2.5">
                        <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br tw-from-rose-500 tw-to-pink-600 tw-flex tw-items-center tw-justify-center tw-shadow-md">
                            <Icon icon="hugeicons:chart-increase" className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h1 className="tw-text-base tw-font-bold tw-text-gray-800">Investments</h1>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Fixed deposits &amp; other investments</p>
                        </div>
                    </div>
                    <button
                        onClick={handleAdd}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-bg-rose-600 tw-text-white hover:tw-bg-rose-700 tw-transition-colors"
                    >
                        <Icon icon="mdi:plus" className="tw-text-sm" />
                        Add Investment
                    </button>
                </div>

                {/* Portfolio summary (active investments) */}
                {summary && (
                    <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
                        {[
                            { label: 'Total Invested', value: `₹ ${formatCurrency(summary.total_invested)}`, sub: `${summary.active_count} active`, icon: 'mdi:cash-multiple', color: 'tw-bg-rose-500' },
                            { label: 'Current Value', value: `₹ ${formatCurrency(summary.total_current_value)}`, sub: `${summary.gain >= 0 ? '+' : '−'} ₹ ${formatCurrency(Math.abs(summary.gain))}`, subClass: summary.gain >= 0 ? 'tw-text-emerald-600' : 'tw-text-rose-600', icon: 'mdi:chart-line', color: 'tw-bg-indigo-500' },
                            { label: `Maturing in ${summary.maturing_within_days} days`, value: summary.maturing_count, sub: `₹ ${formatCurrency(summary.maturing_amount)}`, icon: 'mdi:calendar-clock', color: 'tw-bg-amber-500', onClick: () => setMaturingSoon(v => !v), active: maturingSoon },
                            { label: 'By Type', value: Object.keys(summary.by_type || {}).length, sub: Object.entries(summary.by_type || {}).map(([t, v]) => `${typeMeta(t).short} ${v.count}`).join(' · ') || '—', icon: 'mdi:shape-outline', color: 'tw-bg-emerald-500' },
                        ].map(card => (
                            <div
                                key={card.label}
                                onClick={card.onClick}
                                className={`tw-rounded-lg tw-border tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-gap-2.5 ${card.onClick ? 'tw-cursor-pointer hover:tw-bg-gray-50' : ''} ${card.active ? 'tw-border-amber-400 tw-bg-amber-50/40' : 'tw-border-gray-100'}`}
                                title={card.onClick ? 'Show only these' : undefined}
                            >
                                <div className={`tw-w-8 tw-h-8 tw-rounded-lg ${card.color} tw-flex tw-items-center tw-justify-center tw-flex-shrink-0`}>
                                    <Icon icon={card.icon} className="tw-text-white tw-text-sm" />
                                </div>
                                <div className="tw-min-w-0">
                                    <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">{card.label}</p>
                                    <p className="tw-text-sm tw-font-bold tw-text-gray-800">{card.value}</p>
                                    <p className={`tw-text-[10px] tw-truncate ${card.subClass || 'tw-text-gray-500'}`}>{card.sub}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Filters */}
                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-bg-gray-50/50">
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-6 tw-gap-3 tw-items-end">
                        <div>
                            <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Type</label>
                            <SearchableDropdown
                                value={investmentType}
                                onChange={setInvestmentType}
                                options={TYPE_OPTIONS}
                                placeholder="Type"
                                clearable={false}
                                small
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Status</label>
                            <SearchableDropdown
                                value={status}
                                onChange={setStatus}
                                options={STATUS_OPTIONS}
                                placeholder="Status"
                                clearable={false}
                                small
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplySearch(); }}
                                placeholder="Number, bank, holder…"
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">From Date</label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">To Date</label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                            />
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <button
                                onClick={handleApplySearch}
                                className="tw-flex-1 tw-px-3 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleRefresh}
                                className="tw-p-1.5 tw-rounded-md tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors"
                                title="Refresh"
                            >
                                <Icon icon="mdi:refresh" className="tw-text-base" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Date</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Type</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Number / Institution</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Invested</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Rate (%)</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Mode</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Frequency</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Maturity Date</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Maturity / Current Value</th>
                                <th className="tw-text-center tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Status</th>
                                <th className="tw-text-center tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-gray-50">
                            {loading ? (
                                <tr>
                                    <td colSpan={11} className="tw-text-center tw-py-12 tw-text-gray-400">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                                            <span className="tw-text-xs">Loading investments...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : investments.length > 0 ? (
                                investments.map(inv => {
                                    const badge = STATUS_BADGE[inv.status] || STATUS_BADGE.active;
                                    const meta = typeMeta(inv.investment_type);
                                    const marketLinked = MARKET_LINKED.includes(inv.investment_type);
                                    const hasRate = inv.interest_rate != null && inv.interest_rate !== '';
                                    return (
                                        <tr key={inv.id} className="hover:tw-bg-slate-50/50 tw-transition-colors">
                                            <td className="tw-px-4 tw-py-3 tw-text-gray-600 tw-whitespace-nowrap">{formatDate(inv.investment_date)}</td>
                                            <td className="tw-px-4 tw-py-3">
                                                <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-whitespace-nowrap ${meta.color}`}>
                                                    <Icon icon={meta.icon} className="tw-text-xs" />{meta.short}
                                                </span>
                                            </td>
                                            <td className="tw-px-4 tw-py-3">
                                                <div className="tw-font-medium tw-text-rose-600">{inv.investment_number}</div>
                                                {(inv.institution_name || inv.branch_name) && (
                                                    <div className="tw-text-[10px] tw-text-gray-500">{[inv.institution_name, inv.branch_name].filter(Boolean).join(', ')}</div>
                                                )}
                                                {(!!inv.auto_renew || !!inv.lien_marked) && (
                                                    <div className="tw-flex tw-gap-1 tw-mt-0.5">
                                                        {!!inv.auto_renew && <span className="tw-text-[9px] tw-px-1 tw-rounded tw-bg-purple-50 tw-text-purple-600">Auto-renew</span>}
                                                        {!!inv.lien_marked && <span className="tw-text-[9px] tw-px-1 tw-rounded tw-bg-amber-50 tw-text-amber-700" title={inv.lien_details || ''}>Lien</span>}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-gray-700">₹ {formatCurrency(inv.principal_amount)}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-right tw-text-gray-700">{hasRate ? `${parseFloat(inv.interest_rate).toFixed(2)}%` : '—'}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-gray-600 tw-capitalize">{INTEREST_MODE_LABELS[inv.interest_mode] || inv.interest_mode || '—'}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-gray-600">{FREQUENCY_LABELS[inv.frequency] || inv.frequency || '—'}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-gray-600 tw-whitespace-nowrap">{formatDate(inv.maturity_date)}</td>
                                            <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium tw-text-emerald-600">
                                                {inv.current_value != null ? (
                                                    <>
                                                        ₹ {formatCurrency(inv.current_value)}
                                                        <div className="tw-text-[10px] tw-text-gray-400 tw-font-normal">value as on {formatDate(inv.current_value_date)}</div>
                                                    </>
                                                ) : inv.maturity_amount != null ? `₹ ${formatCurrency(inv.maturity_amount)}` : '—'}
                                            </td>
                                            <td className="tw-px-4 tw-py-3 tw-text-center">
                                                <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium tw-capitalize tw-border ${badge.bg} ${badge.text} ${badge.border}`}>
                                                    {inv.status?.replace(/_/g, ' ') || 'active'}
                                                </span>
                                            </td>
                                            <td className="tw-px-4 tw-py-3 tw-text-center">
                                                <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
                                                    <button
                                                        onClick={() => handleView(inv.id)}
                                                        className="tw-p-1 tw-rounded tw-bg-rose-50 tw-text-rose-600 hover:tw-bg-rose-100 tw-transition-colors"
                                                        title="View"
                                                    >
                                                        <Icon icon="mdi:eye-outline" className="tw-text-sm" />
                                                    </button>
                                                    {inv.status === 'active' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleEdit(inv.id)}
                                                                className="tw-p-1 tw-rounded tw-bg-blue-50 tw-text-blue-600 hover:tw-bg-blue-100 tw-transition-colors"
                                                                title="Edit"
                                                            >
                                                                <Icon icon="mdi:pencil-outline" className="tw-text-sm" />
                                                            </button>
                                                            <button
                                                                onClick={() => setValuationTarget(inv)}
                                                                className="tw-p-1 tw-rounded tw-bg-indigo-50 tw-text-indigo-600 hover:tw-bg-indigo-100 tw-transition-colors"
                                                                title="Update current value"
                                                            >
                                                                <Icon icon="mdi:chart-line" className="tw-text-sm" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleAction('mature', inv)}
                                                                className="tw-p-1 tw-rounded tw-bg-emerald-50 tw-text-emerald-600 hover:tw-bg-emerald-100 tw-transition-colors"
                                                                title={marketLinked ? 'Redeem / Sell' : 'Mature'}
                                                            >
                                                                <Icon icon="hugeicons:checkmark-circle-02" className="tw-text-sm" />
                                                            </button>
                                                            {!marketLinked && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleAction('pre_mature', inv)}
                                                                        className="tw-p-1 tw-rounded tw-bg-amber-50 tw-text-amber-600 hover:tw-bg-amber-100 tw-transition-colors"
                                                                        title="Pre-Mature"
                                                                    >
                                                                        <Icon icon="hugeicons:alert-circle" className="tw-text-sm" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleAction('renew', inv)}
                                                                        className="tw-p-1 tw-rounded tw-bg-purple-50 tw-text-purple-600 hover:tw-bg-purple-100 tw-transition-colors"
                                                                        title="Renew"
                                                                    >
                                                                        <Icon icon="hugeicons:refresh" className="tw-text-sm" />
                                                                    </button>
                                                                </>
                                                            )}
                                                            <button
                                                                onClick={() => handleDelete(inv)}
                                                                className="tw-p-1 tw-rounded tw-bg-red-50 tw-text-red-500 hover:tw-bg-red-100 tw-transition-colors"
                                                                title="Delete"
                                                            >
                                                                <Icon icon="mdi:trash-can-outline" className="tw-text-sm" />
                                                            </button>
                                                        </>
                                                    )}
                                                    {inv.status === 'matured' && !marketLinked && (
                                                        <button
                                                            onClick={() => handleAction('renew', inv)}
                                                            className="tw-p-1 tw-rounded tw-bg-purple-50 tw-text-purple-600 hover:tw-bg-purple-100 tw-transition-colors"
                                                            title="Renew"
                                                        >
                                                            <Icon icon="hugeicons:refresh" className="tw-text-sm" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={11} className="tw-text-center tw-py-16">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <Icon icon="hugeicons:chart-increase" className="tw-text-3xl tw-text-gray-300" />
                                            <p className="tw-text-xs tw-text-gray-400">No investments found.</p>
                                            <p className="tw-text-xs tw-text-gray-400">
                                                Click "Add Investment" to get started.
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add / Edit Modal */}
            {showModal && (
                <InvestmentModal
                    editId={editId}
                    onClose={handleCloseModal}
                    onSaved={handleSaveSuccess}
                />
            )}

            {/* View Modal */}
            {viewId && (
                <InvestmentViewModal
                    investmentId={viewId}
                    onClose={() => setViewId(null)}
                    onEdit={handleEdit}
                />
            )}

            {/* Action Modal (Mature / Pre-Mature / Renew) */}
            {actionState.action && actionState.investment && (
                <InvestmentActionModal
                    action={actionState.action}
                    investment={actionState.investment}
                    onClose={() => setActionState({ action: null, investment: null })}
                    onSuccess={handleActionSuccess}
                />
            )}

            {/* Current value (mutual funds, shares, ...) */}
            {valuationTarget && (
                <InvestmentValuationModal
                    investment={valuationTarget}
                    onClose={() => setValuationTarget(null)}
                    onSaved={() => { setValuationTarget(null); fetchList(); }}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteTarget && (
                <InvestmentDeleteModal
                    investment={deleteTarget}
                    onClose={() => setDeleteTarget(null)}
                    onSuccess={handleDeleteSuccess}
                />
            )}
        </div>
    );
};

export default Investments;
